﻿/* global runtime */
// Kontrollert bokføring av banklinjer.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const bankPurchaseEditorLineValues = ({ line = {}, purchaseGross = 0, vatRate = 0 } = {}) => {
  const reverseChargeCode = line.reverseChargeCode || line.aiSuggestedReverseChargeCode || (line.reverseCharge === true ? '86' : '');
  const vatFactor = window.PlummePurchaseVatTreatment?.grossFactor({ vatRate, reverseChargeCode }) ?? (reverseChargeCode === '86' ? 1 : 1 + num(vatRate) / 100);
  return {
    net: num(purchaseGross) / vatFactor,
    vat: num(vatRate),
    reverse: reverseChargeCode || undefined,
  };
};

const bankLineTextHasDigits = (description, wanted) => {
  const id = String(wanted || '').replace(/\D/g, '');
  if (!id) return false;
  return String(description || '').replace(/\D+/g, ' ').trim().split(/\s+/).includes(id);
};

const bankLineSettlementPayoutAmount = (analysis = {}) => {
  const explicit = num(analysis.settlementPayoutAmount ?? analysis.netBankAmount ?? analysis.bankAmount);
  if (explicit) return explicit;
  return Math.max(0, num(analysis.settlementGrossAmount || analysis.totalGross) - Math.max(0, num(analysis.settlementFeeAmount)));
};

const bankLineSettlementReceiptForInvoicePayment = ({ state, line, invoiceId } = {}) => {
  if (!invoiceId || !(num(line?.amount) > 0)) return null;
  const invoice = safeArray(state?.invoices).find((entry) => String(entry?.id || '') === String(invoiceId));
  if (!invoice) return null;
  const invoiceRemaining = invoicePaymentSummary(state, invoice).remainingAmount;
  const lineDate = String(line?.date || '').slice(0, 10);
  const matches = safeArray(state?.receiptInbox)
    .filter((entry) => !['booked', 'trashed'].includes(String(entry?.status || '')))
    .map((entry) => ({ entry, analysis: entry?.receiptAnalysis || entry?.analysis || {} }))
    .filter(({ analysis }) => analysis?.status === 'completed' && analysis?.documentType === 'settlement_report')
    .filter(({ analysis }) => Math.abs(bankLineSettlementPayoutAmount(analysis) - num(line.amount)) <= 0.01)
    .filter(({ analysis }) => Math.abs(num(analysis.settlementGrossAmount || analysis.totalGross) - invoiceRemaining) <= 0.01)
    .filter(({ analysis }) => bankLineTextHasDigits(line.description, analysis.settlementId))
    .filter(({ analysis }) => {
      const reportDate = String(analysis.receiptDate || '').slice(0, 10);
      const lastDate = reportDate ? addDays(reportDate, 7) : '';
      return reportDate && lineDate && lineDate >= reportDate && (!lastDate || lineDate <= lastDate);
    });
  return matches.length === 1 ? matches[0].entry : null;
};

const bookBankStatementLineNow = async (statementId, lineId) => {
  const state = mergeState(runtime.appState);
  const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
  if (!statement) throw new Error(t('svc.stmtNotFound'));
  await assertCanBookBankStatement(statementId, lineId);
  const line = safeArray(statement.lines).find((entry) => entry.id === lineId);
  if (!line) throw new Error(t('svc.bankLineNotFound'));
  const configuredAccount = [...safeArray(state?.settings?.bankAccounts), ...safeArray(state?.settings?.paymentAccounts)]
    .find((entry) => String(entry?.id || '') === String(statement.bankAccountId || ''));
  const currency = String(statement.currency || configuredAccount?.currency || 'NOK').toUpperCase();
  if (currency !== 'NOK') throw new Error(t('svc.foreignCurrencyBankPosting'));
  if (line.bankMatchGroupId) return bookBankMatchGroup(line.bankMatchGroupId);
  if (line.matchedEntryId) return line;
  if (line.classification && !['business', 'drift'].includes(line.classification)) {
    throw new Error(t('svc.onlyOperatingLines'));
  }
  const amount = Math.abs(num(line.amount));
  if (!amount) throw new Error(t('svc.bankLineNoAmount'));
  const date = line.date || todayIso();
  const description = line.description || 'Banklinje';
  const trace = bankLineTrace(statement, line);
  const receipt = bankLineReceipt(line);
  const linkedReceiptEntry = safeArray(state.receiptInbox).find((entry) => entry?.id === (line.receiptInboxEntryId || line.aiSuggestedReceiptInboxEntryId));
  const receiptReviewBlockReason = window.apMonthControlReceiptQuickApprovalBlockReason?.(linkedReceiptEntry) || '';
  const bookingType = line.bookingType || (num(line.amount) > 0 ? 'other-sale' : 'purchase');
  const vatRate = line.vatRate ?? line.aiSuggestedVatRate ?? 0;
  const accountCode = line.accountCode || line.aiSuggestedAccountCode || '';
  const netAmount = amount / (1 + num(vatRate) / 100);
  const bankAccountCode = bankStatementLedgerAccountCode(state, statement, '');
  if (!bankAccountCode) throw new Error(t('r1e.booking.bankAccountMissing'));

  if (bookingType === 'existing-entry') {
    if (!line.matchedEntryType || !line.matchedEntryId) {
      throw new Error(t('svc.pickExistingEntry'));
    }
    const currencyNote = await settleForeignCurrencyDifference({
      entryType: line.matchedEntryType,
      entryId: line.matchedEntryId,
      actualNok: amount,
      date,
    });
    await updateBankLineAfterBooking(
      statement.id,
      line.id,
      bankLineBookedPatch(line.matchedEntryType, line.matchedEntryId, currencyNote || 'Avstemt mot eksisterende post.'),
    );
    return { id: line.matchedEntryId, type: line.matchedEntryType };
  }

  if (bookingType === 'invoice-payment') {
    if (!line.invoiceId) throw new Error(t('svc.pickInvoiceFirst'));
    const settlementReceiptEntry = linkedReceiptEntry || bankLineSettlementReceiptForInvoicePayment({ state, line, invoiceId: line.invoiceId });
    const receiptAnalysis = settlementReceiptEntry?.receiptAnalysis || settlementReceiptEntry?.analysis || {};
    const settlementReport = receiptAnalysis?.status === 'completed' && receiptAnalysis?.documentType === 'settlement_report';
    const paymentGross = settlementReport ? num(receiptAnalysis.settlementGrossAmount || receiptAnalysis.totalGross) : amount;
    const feeAmount = settlementReport ? Math.max(0, num(receiptAnalysis.settlementFeeAmount)) : 0;
    const bankAmount = settlementReport ? bankLineSettlementPayoutAmount(receiptAnalysis) : undefined;
    const reference = settlementReport
      ? [receiptAnalysis.settlementProvider || 'Oppgjør', receiptAnalysis.settlementId].filter(Boolean).join(' ')
      : description;
    const payment = await addInvoicePayment(line.invoiceId, {
      id: `payment-bank-${statement.id}-${line.id}`,
      date,
      amount: paymentGross,
      feeAmount,
      feeAccountCode: feeAmount > 0 ? '7770' : undefined,
      bankAmount,
      settlementAccountCode: bankAccountCode,
      reference,
      note: `Bankavstemming: ${statement.fileName || statement.originalFileName || description}`,
      createdByFlow: 'bank-reconciliation',
      receiptInboxEntryId: settlementReceiptEntry?.id,
      bankTrace: trace,
    });
    await updateBankLineAfterBooking(statement.id, line.id, {
      ...bankLineBookedPatch('payment', payment?.id, 'Bokført som fakturabetaling fra bankavstemming.'),
      receiptInboxEntryId: settlementReceiptEntry?.id,
      attachmentName: settlementReceiptEntry?.attachmentName || settlementReceiptEntry?.name,
      attachmentId: settlementReceiptEntry?.attachmentId || settlementReceiptEntry?.id,
      attachmentMimeType: settlementReceiptEntry?.attachmentMimeType,
      attachmentDownloadUrl: safeArray(settlementReceiptEntry?.attachments)[0]?.downloadUrl || settlementReceiptEntry?.downloadUrl || settlementReceiptEntry?.file,
      documentationMode: settlementReceiptEntry?.id ? 'receipt' : undefined,
    });
    return payment;
  }

  if (bookingType === 'rounding' || bookingType === 'bank-fee' || bookingType === 'loan-debt' || bookingType === 'owner-settlement') {
    const posting = await upsertBankReconciliationPosting(buildControlledBankLinePosting({ bookingType, statementId: statement.id, lineId: line.id, date, description, amount: line.amount, accountCode, bankAccountCode, bankTrace: trace, loanPrincipalAmount: line.loanPrincipalAmount, loanInterestAmount: line.loanInterestAmount, loanFeeAmount: line.loanFeeAmount }));
    const note = bookingType === 'rounding'
      ? 'Bokført som ørediff fra bankavstemming.'
      : bookingType === 'bank-fee'
        ? 'Bokført som bankgebyr fra bankavstemming.'
        : bookingType === 'loan-debt'
          ? num(line.amount) < 0 ? 'Bokført som avdrag på lån fra bankavstemming.' : 'Bokført som låneopptak fra bankavstemming.'
          : num(line.amount) < 0 ? 'Bokført som privat uttak/eierføring fra bankavstemming.' : 'Bokført som privat innskudd/eierføring fra bankavstemming.';
    await updateBankLineAfterBooking(statement.id, line.id, bankLineBookedPatch('bank-posting', posting.id, note));
    return posting;
  }

  if (bookingType === 'purchase' || bookingType === 'supplier-invoice') {
    if (!accountCode) throw new Error(t('r1e.booking.pickExpenseAccount'));
    if (receiptReviewBlockReason && line.receiptReviewConfirmed !== true) throw new Error(receiptReviewBlockReason);
    if (bookingType === 'supplier-invoice' && !String(line.supplierInvoiceNumber || '').trim()) {
      throw new Error(t('r1e.booking.invoiceNumberRequired'));
    }
    // Banklinjen er det som BLE BETALT. Fakturaen kan vaere storre: en klient som
    // betaler 30 000 av en faktura pa 50 000 skal ha en faktura pa 50 000 med
    // 20 000 utestaaende â€” ikke et kjop pa 30 000 som ser fullt betalt ut.
    // Uten dette forsvant restgjelden fra leverandorreskontroen.
	    const invoiceGross = Math.max(0, num(line.supplierInvoiceGross));
	    const isPartial = bookingType === 'supplier-invoice' && invoiceGross > amount + 0.005;
	    const purchaseGross = isPartial ? invoiceGross : amount;
	    const purchaseAnalysis = linkedReceiptEntry?.receiptAnalysis || linkedReceiptEntry?.analysis || {};
	    const documentNet = Math.round(num(purchaseAnalysis.documentNetAmount) * 100) / 100;
	    const documentVat = Math.round(num(purchaseAnalysis.documentVatAmount ?? purchaseAnalysis.vatAmount) * 100) / 100;
	    const invoiceRounding = Math.round(num(purchaseAnalysis.roundingAmount) * 100) / 100;
	    const hasDocumentAmounts = documentNet > 0 && documentVat >= 0
	      && Math.abs(Math.round((documentNet + documentVat + invoiceRounding) * 100) / 100 - purchaseGross) <= 0.02;
	    const purchaseLineValues = hasDocumentAmounts
	      ? { net: documentNet, vat: num(purchaseAnalysis.documentVatRate ?? purchaseAnalysis.vatRate ?? vatRate), vatAmount: documentVat, reverse: purchaseAnalysis.reverseChargeCode || undefined }
	      : bankPurchaseEditorLineValues({ line, purchaseGross, vatRate });
    const purchase = buildPurchaseFromEditor({
      id: `expense-bank-${statement.id}-${line.id}`,
      date,
      description,
      supplierId: line.supplierId || undefined,
      supplierName: line.supplierName || undefined,
      supplierOrganizationNumber: line.supplierOrgNumber || undefined,
      invoiceNumber: line.supplierInvoiceNumber || undefined,
      // Forfallsdatoen er leverandorens egen, og den skal folge bilaget. Uten
      // den faller `dueDate` tilbake til betalingsdatoen, og fakturaen star
      // dokumentert med en frist leverandoren aldri satte. Betaler banklinjen
      // bare en del av fakturaen, er den dessuten fortsatt en apen post â€” og da
      // er fristen det aldersfordelingen regner fra.
      dueDate: line.supplierInvoiceDueDate || undefined,
      kjopType: bookingType === 'supplier-invoice' ? 'Registrer faktura' : 'Kjøp',
      isPaid: true,
      payDate: date,
      payAcct: bankAccountCode,
      // Delbetaling: det banklinjen dekker foeres som en betaling, og redaktoren
      // legger selv resten paa 2400. Da blir fakturaen staaende som apen post
      // med riktig restbelop og riktig forfallsdato.
      partPayments: isPartial
        ? [{ id: `part-${line.id}`, amount, date, account: bankAccountCode }]
        : undefined,
      receipt,
      documentGrossAmount: hasDocumentAmounts ? purchaseGross : undefined,
      roundingAmount: hasDocumentAmounts ? invoiceRounding : undefined,
      roundingAccountCode: hasDocumentAmounts && invoiceRounding ? '7740' : undefined,
      lines: [{
        id: `line-${line.id}`,
        desc: description,
	        net: purchaseLineValues.net,
	        vat: purchaseLineValues.vat,
	        vatAmount: purchaseLineValues.vatAmount,
	        costAccountCode: accountCode,
	        reverse: purchaseLineValues.reverse,
      }],
    }, 'active');
    const saved = await upsertPurchase(stripUndefinedDeep({ ...purchase, bankTrace: trace, source: 'bank' }));
    await updateBankLineAfterBooking(statement.id, line.id, bankLineBookedPatch('expense', saved.id, 'Bokført som kjøp fra bankavstemming.'));
    return saved;
  }

  if (bookingType === 'other-sale' || bookingType === 'sale') {
    const sale = buildOtherSaleFromEditor({
      sourceDraftId: `other-sale-bank-${statement.id}-${line.id}`,
      invDate: date,
      descr: description,
      lines: [{
        id: `line-${line.id}`,
        desc: description,
        qty: 1,
        price: netAmount,
        vat: vatRate ? String(vatRate) : '0',
      }],
      attachments: receipt.attachmentId ? [receipt] : [],
    }, 'completed');
    const saved = await upsertOtherSale(stripUndefinedDeep({
      ...sale,
      salesAccountCode: accountCode || sale.salesAccountCode || (vatRate ? '3000' : '3100'),
      settlementAccountCode: bankAccountCode,
      paymentSource: 'company',
      receiptInboxEntryId: receipt.id,
      attachmentId: receipt.attachmentId,
      attachmentName: receipt.attachmentName,
      attachmentMimeType: receipt.attachmentMimeType,
      bankTrace: trace,
      source: 'bank',
    }));
    await updateBankLineAfterBooking(statement.id, line.id, bankLineBookedPatch('other-sale', saved.id, 'Bokført som annet salg fra bankavstemming.'));
    return saved;
  }

  if (bookingType === 'free-posting') {
    const suppliedLines = safeArray(line.manualPostingLines);
    const candidateLines = suppliedLines.length ? suppliedLines : [{
      fromAccountCode: line.fromAccountCode || (num(line.amount) > 0 ? accountCode : bankAccountCode),
      toAccountCode: line.toAccountCode || accountCode,
      amount,
      fromVatCode: line.fromVatCode || bankVatCodeFromRate(line.fromVatRate),
      toVatCode: line.toVatCode || bankVatCodeFromRate(line.toVatRate),
    }];
    const checked = window.PlummeBankFreePostingLines?.validate({ lines: candidateLines, bankAccountCode, bankAmount: num(line.amount) });
    if (!checked?.ok) {
      if (checked?.reason === 'bank-mismatch') throw new Error(t('bank.manualPostingBankMismatch'));
      throw new Error(t('svc.pickTwoAccounts'));
    }
    // Banksporet maa folge selve posteringen inn i mutasjonen. `expenses` er
    // server-eid og strippes fra klientskrivinger, saa en etterfolgende
    // updateStateAndPersist ville bare satt sporet i minnet — og posteringen
    // dukket opp igjen som «mulig avvik» ved neste state fra serveren.
    const saved = await saveBankManualPosting({
      id: window.PlummeBankFreePostingLines.postingId({ expenses: state.expenses, statementId: statement.id, lineId: line.id }),
      date,
      desc: description,
      bankTrace: trace,
      lines: checked.lines.map((postingLine, index) => ({
        id: postingLine.id || `line-${line.id}-${index + 1}`,
        fraKonto: postingLine.fromAccountCode,
        fraBelop: postingLine.amount,
        fraMva: postingLine.fromVatCode || 'none',
        tilKonto: postingLine.toAccountCode,
        tilBelop: postingLine.amount,
        tilMva: postingLine.toVatCode || 'none',
      })),
    });
    return saved;
  }

  if (bookingType === 'vat-payment') {
    const year = parseBankTermYear(line.taxTerm, state.activeYear);
    const termKey = parseVatTermKey(line.taxTerm);
    const settlementKind = num(line.amount) > 0 ? 'refund' : 'payment';
    const record = {
      id: `mva-payment-bank-${statement.id}-${line.id}`,
      amount,
      expectedAmount: amount,
      date,
      source: 'bank',
      settlementKind,
      expectedSettlementKind: settlementKind,
      settlementAccountCode: bankAccountCode,
      note: description,
    };
    await saveVatPaymentRecord({ year, termKey, record });
    await updateBankLineAfterBooking(statement.id, line.id, bankLineBookedPatch('vat-payment', record.id, 'Bokført som MVA-betaling fra bankavstemming.'));
    return record;
  }

  if (bookingType === 'payroll-tax') {
    if (num(line.amount) >= 0) {
      throw new Error(t('svc.incomingPayrollTaxPayment'));
    }
    if (payrollTaxBankBookingRequiresPayrollFlow(state, line)) {
      throw new Error(t('r1e.booking.payrollTaxFlow'));
    }
    const part = String(line.taxPaymentPart || '').trim();
    if (!['forskudd', 'aga'].includes(part)) {
      throw new Error(t('svc.splitPayrollPayment'));
    }
    const liabilityAccountCode = part === 'aga' ? '2770' : '2600';
    const posting = await upsertBankReconciliationPosting({
      id: `bank-posting-${statement.id}-${line.id}`,
      date,
      description,
      amount: num(line.amount),
      debitAccountCode: liabilityAccountCode,
      creditAccountCode: bankAccountCode,
      bookingType,
      bankTrace: trace,
    });
    await updateBankLineAfterBooking(statement.id, line.id, bankLineBookedPatch('bank-posting', posting.id, 'Bokført som forskuddstrekk/AGA fra bankavstemming.'));
    return posting;
  }

  if (bookingType === 'tax-payment') {
    if (num(line.amount) >= 0) {
      throw new Error(t('svc.incomingTaxPayment'));
    }
    const taxAccountCode = resolveBankTaxPaymentAccountCode(state, line);
    const posting = await upsertBankReconciliationPosting({
      id: `bank-posting-${statement.id}-${line.id}`,
      date,
      description,
      amount: num(line.amount),
      debitAccountCode: taxAccountCode,
      creditAccountCode: bankAccountCode,
      bookingType,
      bankTrace: trace,
    });
    await updateBankLineAfterBooking(statement.id, line.id, bankLineBookedPatch('bank-posting', posting.id, 'Bokført som skattebetaling fra bankavstemming.'));
    return posting;
  }

  const debitAccountCode = num(line.amount) < 0 ? (accountCode || line.toAccountCode || '7790') : bankAccountCode;
  const creditAccountCode = num(line.amount) < 0 ? bankAccountCode : (accountCode || line.fromAccountCode || '3900');
  const posting = await upsertBankReconciliationPosting({
    id: `bank-posting-${statement.id}-${line.id}`,
    date,
    description,
    amount: num(line.amount),
    debitAccountCode,
    creditAccountCode,
    bookingType,
    bankTrace: trace,
  });
  await updateBankLineAfterBooking(statement.id, line.id, bankLineBookedPatch('bank-posting', posting.id, 'Bokført som kontrollert bankpostering.'));
  return posting;
};

// Bokforingen gaar gjennom tre serverkall for banklinjen er merket bokfort. Er
// nettet tregt, staar knappen aapen hele veien, og et nytt klikk startet hele
// kjeden paa nytt: `line.matchedEntryId` — vaktposten mot dobbeltforing — hadde
// ikke rukket aa lande i state enda. Andre klikk skal vente paa det forste.
const bankLineBookingsInFlight = new Map();
const bookBankStatementLine = (statementId, lineId) => {
  const key = `${statementId}:${lineId}`;
  const pending = bankLineBookingsInFlight.get(key);
  if (pending) return pending;
  const booking = bookBankStatementLineNow(statementId, lineId)
    .finally(() => { bankLineBookingsInFlight.delete(key); });
  bankLineBookingsInFlight.set(key, booking);
  return booking;
};
